import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import {
  Send,
  ArrowUpRight,
} from "lucide-react";
import { Card, Kbd, cn } from "@nexirt/ui";
import { getOrCreateSessionId } from "../lib/copilot-session";
import { sendCopilotMessage, CopilotError } from "../lib/copilot-fetch";
import { CHIP_PREFILL_EVENT } from "../lib/copilot-chips";
import { mountTurnstile, useTurnstileToken, whenTurnstileReady, TURNSTILE_SITE_KEY } from "../lib/turnstile";
import { AuthWall } from "./AuthWall";
import { MarkdownMessage } from "./MarkdownMessageLazy";
import { TurnstileBadge } from "./TurnstileBadge";

const MAX_LEN = 2000;
const MAX_INLINE_TURNS = 3;
const PLACEHOLDER_IDLE = "We need a payroll dashboard for 12 sites...";
const PLACEHOLDER_FOLLOWUP = "Ask a follow-up…";
const PLACEHOLDER_PENDING = "Copilot is thinking…";

// Rev 25.5 retired the inline CHIPS array from this file. Chip data
// + types live in `apps/web/src/lib/copilot-chips.ts`; chip render
// lives in `apps/web/src/components/CopilotComposerChips.tsx`. This
// composer listens on the `CHIP_PREFILL_EVENT` window event below to
// prefill its textarea — works no matter where the chip render site
// is mounted in the DOM.

type Turn = { role: "user" | "assistant"; content: string };

type ComposerState =
  | { kind: "idle" }
  | { kind: "submitting"; pending: string }
  | { kind: "reply-shown"; turns: Turn[] }
  | { kind: "submitting-followup"; turns: Turn[]; pending: string }
  | { kind: "auth-wall"; turns: Turn[] };

declare global {
  interface WindowEventMap {
    "nexirt:copilot:open": CustomEvent<{ prompt?: string }>;
    "nexirt:copilot:preload": CustomEvent<{
      sessionId: string;
      turns: Turn[];
    }>;
    "nexirt:copilot:show-panel": CustomEvent<{
      slug: "kpis" | "pricing" | "process" | "calendar" | "proof";
    }>;
  }
}

/**
 * Detect which DynamicOutput panel a prompt most likely wants to surface.
 * Cheap keyword match for Phase 2; Phase 4 wires Anthropic tool-use so
 * the model picks the panel directly. Returns null if no clear match.
 */
function detectPanel(prompt: string): "kpis" | "pricing" | "process" | "calendar" | "proof" | null {
  const p = prompt.toLowerCase();
  if (/\b(price|pricing|cost|budget|how much|estimate|tier)\b/.test(p)) return "pricing";
  if (/\b(book|call|calendar|schedule|meeting|availability|slot)\b/.test(p)) return "calendar";
  if (/\b(process|deliver|methodology|how do you|how does it work|stages)\b/.test(p)) return "process";
  if (/\b(kpi|dashboard|metric|chart|sparkline|ops|dispatch)\b/.test(p)) return "kpis";
  if (/\b(trust|credibility|uptime|review|why you|proof|case stud|track record)\b/.test(p)) return "proof";
  return null;
}

export function CopilotComposer() {
  const [value, setValue] = useState("");
  const [state, setState] = useState<ComposerState>({ kind: "idle" });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const sessionIdRef = useRef<string>("");
  const abortRef = useRef<AbortController | null>(null);
  // Rev 27 Loop A — transcript scroll container ref. Bounded
  // max-h so the page total height doesn't balloon with conversation
  // length, plus an auto-scroll-to-bottom effect on each new turn so
  // the user always sees the latest reply without manually scrolling.
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  // Rev 26.5 — Turnstile widget. Mounted once into a sibling
  // container; the token is shared module-side with ChatWidget so
  // both surfaces consume the same challenge response (visitor only
  // sees one challenge per page load).
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const turnstileToken = useTurnstileToken();

  useEffect(() => {
    sessionIdRef.current = getOrCreateSessionId();
    return () => abortRef.current?.abort();
  }, []);

  useEffect(() => {
    whenTurnstileReady(() => {
      if (turnstileRef.current) mountTurnstile(turnstileRef.current);
    });
  }, []);

  // Auto-grow textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [value, state.kind]);

  // Rev 27 Loop A — derived "has the user started chatting?" boolean.
  // Recomputed below to share with the chat-started dataset attribute
  // and the auto-scroll dep; declared once so both effects pick up the
  // same value. (renderedTurns is computed after this block, so we
  // read state.kind directly here.)
  const hasStartedChat =
    state.kind === "submitting" ||
    state.kind === "submitting-followup" ||
    state.kind === "reply-shown" ||
    state.kind === "auth-wall";

  const turnsSoFar: Turn[] =
    state.kind === "reply-shown"
      ? state.turns
      : state.kind === "submitting-followup"
      ? state.turns
      : state.kind === "auth-wall"
      ? state.turns
      : [];
  const userTurnsSoFar = turnsSoFar.filter((t) => t.role === "user").length;
  const atInlineCap = userTurnsSoFar >= MAX_INLINE_TURNS;
  const isPending =
    state.kind === "submitting" || state.kind === "submitting-followup";
  const inAuthWall = state.kind === "auth-wall";

  const submit = useCallback(async () => {
    const prompt = value.trim();
    if (!prompt || isPending || atInlineCap) return;

    setErrorMsg(null);

    const sessionId = sessionIdRef.current || getOrCreateSessionId();
    sessionIdRef.current = sessionId;

    // Resolve the recovery path BEFORE mutating state — TypeScript narrows
    // `state` based on these reads; later references to "submitting" /
    // "submitting-followup" would be unreachable types after this point.
    const fromIdle = state.kind === "idle";
    const fromReplyShown = state.kind === "reply-shown";
    if (!fromIdle && !fromReplyShown) return;
    const priorTurns: Turn[] = fromReplyShown ? state.turns : [];

    if (fromIdle) {
      setState({ kind: "submitting", pending: prompt });
    } else {
      setState({
        kind: "submitting-followup",
        turns: priorTurns,
        pending: prompt,
      });
    }
    setValue("");

    // Phase 2 visual hand-off: scroll to + pulse the matching panel in
    // DynamicOutput while the Anthropic call is in flight. Phase 4 will
    // replace this with real tool-use selection from the model.
    const panelSlug = detectPanel(prompt);
    if (panelSlug) {
      window.dispatchEvent(
        new CustomEvent("nexirt:copilot:show-panel", {
          detail: { slug: panelSlug },
        }),
      );
    }

    abortRef.current = new AbortController();
    try {
      const r = await sendCopilotMessage({
        sessionId,
        message: prompt,
        signal: abortRef.current.signal,
        turnstileToken,
      });
      const userTurn: Turn = { role: "user", content: prompt };
      const assistantTurn: Turn = { role: "assistant", content: r.reply };
      setState({
        kind: "reply-shown",
        turns: [...priorTurns, userTurn, assistantTurn],
      });
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      const ce = err as CopilotError;
      // Rev 25 Phase 3 Loop P3-F — 401 auth_required surfaces an inline
      // AuthWall instead of bouncing to the widget. The wall replaces the
      // composer body until the user clicks "Sign in" (router-navigates)
      // or "Maybe later" (resets to idle / reply-shown).
      if (ce.code === "auth_required") {
        setState({ kind: "auth-wall", turns: priorTurns });
        setValue("");
        return;
      }
      setErrorMsg(ce.message ?? "Something went wrong. Try once more.");
      if (fromIdle) {
        setState({ kind: "idle" });
      } else {
        setState({ kind: "reply-shown", turns: priorTurns });
      }
      setValue(prompt);
    }
  }, [value, state, isPending, atInlineCap]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!atInlineCap) submit();
      }
    },
    [submit, atInlineCap],
  );

  // Rev 25.2 — chip click pre-fills the textarea with the FULL prompt
  // sentence (not the short label), verbatim from docs/NEXIRT_DESIGN/
  // Home.html chip data-prompt attributes. User can edit before sending.
  // Rev 25.5 — chip render lives in <CopilotComposerChips />; this
  // composer subscribes to CHIP_PREFILL_EVENT so the UX is identical
  // regardless of where the chip render mounts in the DOM.
  const onChip = useCallback((prompt: string) => {
    setValue(prompt);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(prompt.length, prompt.length);
    });
  }, []);

  useEffect(() => {
    function handler(e: WindowEventMap[typeof CHIP_PREFILL_EVENT]) {
      const p = e.detail?.prompt;
      if (typeof p === "string" && p.length > 0) onChip(p);
    }
    window.addEventListener(CHIP_PREFILL_EVENT, handler);
    return () => window.removeEventListener(CHIP_PREFILL_EVENT, handler);
  }, [onChip]);

  const continueInWidget = useCallback(() => {
    const turns =
      state.kind === "reply-shown" || state.kind === "submitting-followup"
        ? state.turns
        : [];
    window.dispatchEvent(
      new CustomEvent("nexirt:copilot:preload", {
        detail: { sessionId: sessionIdRef.current, turns },
      }),
    );
  }, [state]);

  const placeholder = isPending
    ? PLACEHOLDER_PENDING
    : state.kind === "idle"
    ? PLACEHOLDER_IDLE
    : PLACEHOLDER_FOLLOWUP;

  const chipsVisible = state.kind === "idle";
  const composerInputDisabled = isPending || atInlineCap || inAuthWall;

  // Pending turn rendered visually inside the transcript while submitting-followup.
  const renderedTurns: Turn[] =
    state.kind === "submitting-followup"
      ? [...state.turns, { role: "user", content: state.pending }]
      : state.kind === "submitting"
      ? [{ role: "user", content: state.pending }]
      : turnsSoFar;

  // Rev 27 Loop A — expose chat-started signal globally via the
  // documentElement dataset attribute. Hero right-column (Spline robot
  // + chip grid) reads `[data-chat-started="1"]` from index.css and
  // fades to opacity 0.30 + pointer-events:none so the chat zone
  // visually dominates once the user has engaged. Using a dataset
  // attribute on <html> avoids prop-drilling state into Home.tsx (the
  // composer and the robot/chips are sibling grid cells, not parent/
  // child) and lets the fade rule live in CSS, not JS state.
  useEffect(() => {
    if (hasStartedChat) {
      document.documentElement.dataset.chatStarted = "1";
    } else {
      delete document.documentElement.dataset.chatStarted;
    }
    return () => {
      delete document.documentElement.dataset.chatStarted;
    };
  }, [hasStartedChat]);

  // Rev 27 Loop A — auto-scroll transcript to bottom on each new turn.
  // The transcript is its own overflow-y-auto container (max-h-[55vh])
  // so this scrolls inside the container, not the page.
  useEffect(() => {
    const el = transcriptRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [renderedTurns.length, isPending]);

  return (
    <div className="w-full" data-copilot-composer data-state={state.kind}>
      <style>{COMPOSER_KEYFRAMES}</style>

      <Card className="flex flex-col overflow-hidden border-nx-line-strong bg-nx-surface/90 backdrop-blur-md pb-[env(safe-area-inset-bottom,0px)] sm:pb-0">
        <div className="flex items-center gap-2.5 border-b border-nx-line px-4 py-3">
          <span
            className={cn("nx-copilot-ai-dot", isPending && "is-thinking")}
            data-copilot-ai-dot
            aria-hidden
          />
          {/* Composer header. Model name removed (was "· sonnet 4.5") — the
              model has been upgraded; we don't surface the version in the UI. */}
          <span className="text-sm font-medium text-nx-text">Nexirt assistant</span>
          {isPending ? (
            <span
              className="ml-auto text-xs text-nx-text-faint"
              aria-live="polite"
            >
              thinking…
            </span>
          ) : null}
        </div>

        {/* Rev 27 Loop A — transcript anchored ABOVE the composer in a
            bounded, internally-scrolling container. Owner-reported
            regression: prior layout had transcript BELOW composer + no
            max-height → conversation pushed page height unboundedly,
            composer + content below the hero became unreachable. Now:
            transcript caps at 55vh (60vh on mobile where there's no
            adjacent right column to share viewport with), scrolls
            internally, auto-scrolls to bottom on new turn. Composer
            stays anchored below — always reachable regardless of
            transcript length. */}
        {!inAuthWall && renderedTurns.length > 0 ? (
          <div
            ref={transcriptRef}
            className="max-h-[60vh] overflow-y-auto border-b border-nx-line bg-nx-bg/40 px-4 py-4 md:max-h-[55vh] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--nx-border)]"
            role="log"
            aria-live="polite"
            data-copilot-transcript
          >
            <div className="space-y-2.5">
              {renderedTurns.map((t, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex",
                    t.role === "user" ? "justify-end" : "justify-start",
                  )}
                  data-copilot-turn
                  data-copilot-turn-role={t.role}
                >
                  <div
                    className={cn(
                      "max-w-[85%] rounded-nx2 px-3 py-2 text-sm leading-relaxed",
                      // User turns stay plain-text; user input is
                      // sanitized server-side + we don't want to render
                      // a visitor's Markdown. Assistant turns route
                      // through MarkdownMessage so Anthropic's default
                      // Markdown formatting (bold/lists/code/links)
                      // renders branded instead of raw '**asterisks'.
                      t.role === "user"
                        ? "whitespace-pre-wrap border border-nx-accent/30 bg-nx-accent-soft text-nx-text"
                        : "border border-nx-line bg-nx-surface text-nx-text",
                    )}
                  >
                    {t.role === "user" ? t.content : <MarkdownMessage content={t.content} />}
                  </div>
                </div>
              ))}
              {isPending ? (
                <div className="flex justify-start" data-copilot-typing>
                  <div className="rounded-nx2 border border-nx-line bg-nx-surface px-3 py-2">
                    <span className="inline-flex gap-1">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-nx-text-dim [animation-delay:-0.3s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-nx-text-dim [animation-delay:-0.15s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-nx-text-dim" />
                    </span>
                  </div>
                </div>
              ) : null}
              {errorMsg ? (
                <p
                  className="rounded-nx2 border border-nx-danger/40 bg-[rgba(244,63,94,0.06)] px-3 py-2 text-xs text-nx-danger"
                  role="alert"
                >
                  {errorMsg}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="px-4 pt-4 pb-2">
          <textarea
            ref={textareaRef}
            data-copilot-textarea
            value={value}
            onChange={(e) => setValue(e.target.value.slice(0, MAX_LEN))}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            rows={3}
            maxLength={MAX_LEN}
            disabled={composerInputDisabled}
            className={cn(
              "w-full resize-none bg-transparent text-base leading-snug text-nx-text",
              "placeholder:text-nx-text-faint focus:outline-none",
              "min-h-[72px] max-h-[240px]",
              "disabled:opacity-60",
            )}
            aria-label="NEXIRT Copilot prompt"
          />
          {/* Rev 25 Phase 3 P3-C — honeypot. Real users never see this
              hidden field; bots crawling the form fill every input. The
              server returns 202 with a fake reply on non-empty honeypot,
              without ever calling Anthropic. The submit() helper never
              writes to this — it's an HTML form field that only an
              automated crawler would populate. */}
          <input
            type="text"
            name="hp-website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            data-copilot-honeypot
            className="absolute left-[-9999px] h-0 w-0 opacity-0"
            style={{ position: "absolute" }}
          />
        </div>

        <div className="flex items-center justify-between gap-3 px-4 pb-4 pt-2 sm:pb-3 sm:pt-1">
          <div className="hidden flex-wrap items-center gap-x-3 gap-y-1 text-xs text-nx-text-faint sm:flex">
            <span className="inline-flex items-center gap-1.5">
              <Kbd>⏎</Kbd>
              <span>send</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Kbd>⇧+⏎</Kbd>
              <span>newline</span>
            </span>
            <span aria-hidden className="text-nx-line-strong">·</span>
            <span>End-to-end encrypted</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-nx-text-faint sm:hidden">
            <Kbd>⏎</Kbd>
            <Kbd>⇧+⏎</Kbd>
          </div>

          {atInlineCap ? (
            <button
              type="button"
              data-copilot-continue
              onClick={continueInWidget}
              className={cn(
                "inline-flex h-11 min-w-[44px] items-center gap-1.5 rounded-nx2 px-4 text-sm font-medium",
                "bg-nx-accent-2 text-nx-bg",
                "transition-[transform,opacity] duration-150 ease-nx",
                "hover:translate-y-[-1px]",
              )}
              aria-label="Continue conversation in NEXIRT Copilot widget"
            >
              <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} />
              <span>Continue in Copilot</span>
            </button>
          ) : (
            <button
              type="button"
              data-copilot-send
              onClick={submit}
              disabled={value.trim().length === 0 || isPending}
              className={cn(
                "inline-flex h-11 min-w-[44px] items-center gap-1.5 rounded-nx2 px-4 text-sm font-medium",
                "bg-nx-accent text-white shadow-[var(--nx-shadow-glow)]",
                "transition-[transform,opacity] duration-150 ease-nx",
                "hover:translate-y-[-1px] disabled:cursor-not-allowed disabled:opacity-40",
              )}
              aria-label="Send to NEXIRT Copilot"
            >
              <Send className="h-4 w-4" strokeWidth={2.2} />
              <span>Send</span>
            </button>
          )}
        </div>

        {/* Rev 25 Phase 3 Loop P3-F — inline AuthWall when 401 fires.
            Rev 27 Loop A — transcript moved ABOVE the composer body (see
            above); AuthWall stays here so it replaces the composer
            input area without disrupting the transcript that already
            shows the conversation that hit the 401. */}
        {inAuthWall ? (
          <div className="border-t border-nx-line bg-nx-bg/40 px-4 py-4">
            <AuthWall
              onDismiss={() => {
                if (
                  state.kind === "auth-wall" &&
                  state.turns.length > 0
                ) {
                  setState({ kind: "reply-shown", turns: state.turns });
                } else {
                  setState({ kind: "idle" });
                }
              }}
            />
          </div>
        ) : null}
      </Card>

      <p className="mt-2 px-1 text-xs text-nx-text-faint">
        End-to-end encrypted · no training on your prompt
      </p>

      {/* Rev 26.5 — Turnstile widget mount target. Managed mode + size=
          flexible + appearance=interaction-only means CF renders nothing
          visible 95% of the time; on the occasional challenge it shows
          a low-friction widget here. ~64px reserved height keeps the
          page from jumping when CF decides to paint. The token is
          captured into shared module state for ChatWidget to read.
          Rev 27 Loop C — wrapped in TurnstileBadge so the post-verify
          state shows a small brand-color "Verified human" pill instead
          of the default CF green "Success!" branding. */}
      {TURNSTILE_SITE_KEY ? (
        <div className="mt-2">
          <TurnstileBadge>
            <div
              ref={turnstileRef}
              data-copilot-turnstile
              className="min-h-[40px]"
              aria-hidden="true"
            />
          </TurnstileBadge>
        </div>
      ) : null}

      {/* Rev 26.4 — always-visible error banner. The in-transcript
          errorMsg render only mounts when the transcript pane has
          turns; first-submit errors from idle (e.g., 503 capacity
          kill-switch firing on the very first message) had no
          surface. This banner shows the friendly message regardless
          of state.kind, and clears when the user submits again. */}
      {errorMsg ? (
        <p
          className="mt-2 rounded-nx2 border border-nx-danger/40 bg-[rgba(244,63,94,0.06)] px-3 py-2 text-xs text-nx-danger"
          role="alert"
          data-copilot-error-banner
        >
          {errorMsg}
        </p>
      ) : null}

      {/* Chips moved to <CopilotComposerChips /> sibling component
          in Rev 25.5 (renders below the robot at desktop, below this
          composer at mobile). Composer listens to CHIP_PREFILL_EVENT
          below to keep the same UX — click chip → textarea pre-fills
          → focus — regardless of where the chips mount in the DOM. */}
    </div>
  );
}

const COMPOSER_KEYFRAMES = `
@keyframes nx-copilot-ai-spin {
  to { transform: rotate(360deg); }
}
.nx-copilot-ai-dot {
  position: relative;
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  background: conic-gradient(from 0deg, var(--nx-accent), var(--nx-accent-2), var(--nx-accent));
  animation: nx-copilot-ai-spin 2.6s linear infinite;
  flex-shrink: 0;
}
.nx-copilot-ai-dot.is-thinking {
  animation-duration: 1s;
}
.nx-copilot-ai-dot::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 9999px;
  background: var(--nx-bg-raised);
}
.nx-copilot-ai-dot::before {
  content: "";
  position: absolute;
  inset: 5px;
  border-radius: 9999px;
  background: var(--nx-accent);
  box-shadow: 0 0 10px 2px var(--nx-accent-glow);
}
.nx-copilot-chips {
  transition: opacity 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}
@media (prefers-reduced-motion: reduce) {
  .nx-copilot-ai-dot {
    animation: none !important;
  }
  .nx-copilot-chips {
    transition: none !important;
  }
}
`;

export default CopilotComposer;
